export const dpaSv = `# Personuppgiftsbiträdesavtal: andrum Practice

**Version:** \`practice_dpa_v1\`  
**Gäller från:** 2026-07-29  
**Språk:** Svenska (styrande). Engelsk översättning finns i \`practice-dpa-en.md\`.

Detta biträdesavtal gäller behandling av personuppgifter enligt GDPR artikel 28 när Kunden använder andrum Practice. När Kunden accepterar avtalet i Practice Console sparas version och tidpunkt.

---

## 1. Parter

**Personuppgiftsansvarig (“Kunden” / “Praktik”)**  
Den psykolog, terapeut, mottagning eller juridiska person som skapar ett Practice-konto och bjuder in klienter.

**Personuppgiftsbiträde (“andrum”)**  
Leverantören av tjänsten andrum Practice, under varumärket andrum, tillgänglig via bl.a. \`practice.andrum.app\` och relaterade API:er.

---

## 2. Bakgrund och avtalets plats

1. Kunden använder andrum Practice för att bjuda in klienter, tilldela hemuppgifter/mikroexperiment och följa samtyckesstyrd aktivitet mellan samtal.  
2. andrum tillhandahåller plattformen och behandlar personuppgifter **på uppdrag av Kunden** i den mån data rör Practice-kopplingar, tilldelningar och terapeutens användning.  
3. Detta biträdesavtal (**DPA**) utgör bilaga till användarvillkoren för andrum Practice och gäller när Kunden accepterar DPA i tjänsten eller undertecknar separat.  
4. För klientens egna B2C-konto i andrum (utanför delad Practice-scope) gäller andrums integritetspolicy och klienten är i relevanta delar egen registrerad; se bilaga A om rollfördelning.

---

## 3. Definitioner

Termer i GDPR (EU) 2016/679 har samma betydelse. Därutöver:

| Term | Betydelse |
|------|-----------|
| **Tjänsten** | andrum Practice Console, API och relaterade funktioner |
| **Klient** | Fysisk person som kopplas till praktik via invite och samtycke |
| **Instruktion** | Detta DPA, produktens dokumenterade funktioner och skriftliga anvisningar från Kunden |

---

## 4. Behandlingens föremål (art. 28.3)

| | |
|--|--|
| **Ändamål** | Möjliggöra hemuppgifter, överblick och kommunikation mellan sessioner inom icke-klinisk välmåenderam |
| **Art** | Hosting, lagring, överföring, visning, aggregering, e-post/push, AI-sammanfattning av **delad** data |
| **Varaktighet** | Under avtalsförhållandet + lagstadgad/åtagen lagring, därefter radering eller återlämnande enligt §12 |
| **Kategorier av registrerade** | Klienter kopplade till praktik; terapeuter/medlemmar; ev. inbjudna e-postadresser |
| **Kategorier av uppgifter** | Se bilaga B |

**Uttryckligen utanför standardändamål:** diagnostik, journalföring enligt patientdatalag som ersättning för journal, automatiserad klinisk riskbedömning, behandling av särskilda kategorier utöver vad klienten frivilligt delar inom avgränsad share_scope.

---

## 5. Rollfördelning

1. **Kunden är personuppgiftsansvarig** för behandling som sker för att bedriva sin yrkesverksamhet (t.ex. se klientöversikt, lagra interna anteckningar i Tjänsten, bjuda in klienter).  
2. **andrum är personuppgiftsbiträde** för den behandling andrum utför för Kundens räkning i Tjänsten.  
3. Klienten ger **separat samtycke** till delning med praktik (\`practice_share\`). Utan giltigt samtycke ska andrum inte ge Kunden tillgång till ny klientdata.  
4. andrum får behandla tekniska loggar, säkerhet och fakturering som **eget ansvarig** i den utsträckning lagen kräver eller tjänsteleverans kräver (transparens i integritetspolicy).

---

## 6. andrums skyldigheter

andrum ska:

1. Endast behandla personuppgifter enligt **dokumenterade instruktioner** (detta DPA + produktbeteende), om inte EU/svensk lag kräver annat.  
2. Säkerställa att personer med behörighet har **sekretessåtagande**.  
3. Vidta **lämpliga tekniska och organisatoriska skyddsåtgärder** (bilaga C).  
4. Endast anlita **underbiträden** enligt §8.  
5. Bistå Kunden med svar på **registrerades rättigheter** i den mån det gäller data i Tjänsten, mot skälig ersättning om arbetsbördan är oskälig.  
6. Bistå med **DPIA** och samråd med tillsynsmyndighet i rimlig omfattning.  
7. Vid **personuppgiftsincident** underrätta Kunden utan onödigt dröjsmål, senast inom **48 timmar** efter att andrum blivit medveten, med känd information.  
8. På Kundens val **radera eller återlämna** personuppgifter efter uppdragets slut (§12), om lag inte kräver lagring.  
9. Tillhandahålla information för att visa efterlevnad och möjliggöra **revision** (§11).  
10. Inte använda klientdata för att **träna generella AI-modeller** hos tredje part utan separat skriftligt avtal; AI session brief använder endast delad data och tillfälliga prompts enligt produktpolicy.

---

## 7. Kundens skyldigheter

Kunden ska:

1. Ha laglig grund för behandling och för att ge andrum instruktioner.  
2. Informera klienter om delning och se till att **samtycke** inhämtas i Tjänsten innan känslig delning.  
3. Inte använda Tjänsten som **enda patientjournal** eller i strid med yrkesetiska regler / patientdatalag.  
4. Begränsa åtkomst till behörig personal; skydda inloggning.  
5. Inte ladda upp onödiga särskilda kategorier av uppgifter utanför vad Tjänsten är avsedd för.  
6. Ansvara för innehåll i **interna anteckningar** och meddelanden.  
7. Omedelbart meddela andrum vid misstänkt obehörig användning av kontot.

---

## 8. Underbiträden

1. Kunden ger **generellt förhandsgodkännande** till underbiträden som anges i bilaga D, och till ersättare inom samma kategori (t.ex. annan EU-hostingleverantör) under förutsättning att andrum:  
   - tecknar skriftligt avtal med motsvarande dataskyddsskyldigheter, och  
   - informerar Kunden om **väsentliga byten** via e-post eller status/trust-sida med skälig varsel (minst 14 dagar där praktiskt möjligt), varvid Kunden kan invända av sakliga skäl.  
2. andrum ansvarar för underbiträdens prestation som för egen.

---

## 9. Överföring till tredjeland

1. Primär lagring och drift ska ske inom **EU/EES** (t.ex. Sverige/EU-region).  
2. Om överföring utanför EU/EES krävs ska andrum säkerställa giltig mekanism (t.ex. EU SCC) och informera Kunden där det är relevant.

---

## 10. Säkerhet

andrum vidtar åtgärder enligt bilaga C, inklusive men inte begränsat till: TLS, åtkomstkontroll, RLS/multi-tenant-isolering, loggning av känslig åtkomst (audit), backup enligt hostingplan, och rate limiting.

---

## 11. Revision

1. Kunden får, högst **en gång per kalenderår** (eller vid misstänkt incident), begära skriftlig säkerhets-/efterlevnadsinformation och, vid sakligt behov, revision med 30 dagars varsel under kontorstid.  
2. Revision får inte oskäligt störa verksamheten eller andra kunders data.  
3. Kostnad för extern revision som initieras av Kunden bärs av Kunden om inte väsentliga brister påvisas.

---

## 12. Radering och återlämnande

1. Vid **disconnect** mellan klient och praktik upphör Kundens tillgång till ny data; historik hanteras enligt produktpolicy (default: ingen fortsatt access).  
2. Vid **uppsägning** av Practice-konto: inom **30 dagar** raderas eller anonymiseras praktikspecifik data (medlemskap, invites, interna anteckningar, meddelanden, audit utöver lagkrav), om inte Kunden begär export inom 14 dagar efter uppsägning.  
3. Backup-kopior fasas ut enligt normal backupcykel (normalt ≤ 90 dagar).  
4. Klientens B2C-konto och egen data raderas inte automatiskt när praktik avslutas.

---

## 13. Ansvar

1. Vardera parten ansvarar för skada enligt GDPR art. 82 i den mån parten är skadeståndsskyldig.  
2. Begränsningar i huvudavtalet/användarvillkor gäller i den utsträckning tvingande lag tillåter.  
3. andrum ansvarar inte för Kundens felaktiga instruktioner eller missbruk av Tjänsten.

---

## 14. Avtalstid och uppsägning

Detta DPA gäller så länge andrum behandlar personuppgifter för Kunden enligt Tjänsten, och överlever i tillämpliga delar (sekretess, radering, ansvar) därefter.

---

## 15. Tillämplig lag och tvister

Svensk lag. Tvister prövas av svensk domstol, med Stockholms tingsrätt som första instans om inte tvingande konsumentskydd säger annat (B2B: parterna avtalar Stockholms tingsrätt).

---

## 16. Accept

Kunden accepterar detta DPA genom att:

- kryssa i DPA-ruta och skapa praktik i Practice Console, eller  
- klicka “Acceptera DPA” under Inställningar, eller  
- underteckna separat exemplar.

**Version som sparas i systemet:** \`practice_dpa_v1\`.

---

## Bilaga A: Rollfördelning B2C vs Practice (översikt)

| Data | Ansvarig (typiskt) | andrums roll |
|------|--------------------|--------------|
| Klientens egna check-ins i B2C | andrum (tjänsteavtal med klient) + klient | Personuppgiftsansvarig/biträde enligt integritetspolicy |
| Delad vy till terapeut (share_scope) | **Kunden** för sin användning | **Biträde** |
| Interna terapeutanteckningar | **Kunden** | Biträde (hosting) |
| Fakturering av Practice-abonnemang | andrum | Ansvarig för kunduppgifter i billing |

---

## Bilaga B: Uppgiftskategorier

- Identitet: visningsnamn, användar-id, e-post (konto/inbjudan)  
- Användning: check-in-skalor, experimentstatus, programsteg, tidsstämplar  
- Samtycke: version, tidpunkt  
- Terapeut: interna anteckningar, tilldelningar, meddelanden  
- Teknik: IP i audit (begränsat), enhets-/notis-token om aktiverat  
- **Inte standard:** full coach-chattlogg, fri reflektionstext (om inte klient opt-in)

---

## Bilaga C: Säkerhetsåtgärder (sammanfattning)

- Kryptering under transport (TLS)  
- EU-region hosting (Supabase EU, API Fly \`arn\` eller motsv.)  
- Åtkomst: least privilege, JWT, medlemskapskontroll, RLS  
- Auditlogg för klientdetaljvy  
- Separata miljöer/secrets; service role endast backend  
- Incidentprocess och återställning enligt runbooks  

---

## Bilaga D: Underbiträden (lista kan uppdateras)

| Underbiträde | Ändamål | Region (mål) |
|--------------|---------|--------------|
| Supabase (PostgreSQL/Auth) | Databas, autentisering | EU |
| Fly.io | API-hosting | EU (t.ex. Stockholm) |
| Cloudflare | CDN, DNS, sidor | Edge; origin EU |
| Amazon SES (AWS) | Transaktionell e-post | eu-north-1 (mål) |
| Stripe | Betalning | EU/US med SCC/EU-processer |
| xAI | AI-coach / session brief | Enligt leverantörsavtal; begränsad prompt-data |

Aktuell lista publiceras även på trust-sidan: \`https://andrum.app/trust\`.

---

*andrum Practice DPA \`practice_dpa_v1\`, 2026-07-29*
`;
